import {
  type CreatedByCategory,
  type ExtensionPrompt,
  type Group,
  type Prompt,
} from "@1js/pl-types";
import {
  isExtensionPrompt,
  isLikedPrompt,
  isMicrosoftOrigin,
  isMicrosoftPrompt,
  isNonSharedUserPrompt,
  isTenantPrompt,
  isWorkgroupPrompt,
} from "@1js/pl-utils";
import {
  CreatedByKey,
  DepartmentKey,
  DisplayCategoryKey,
  EducationKey,
  ExtensionKey,
  IndustryKey,
  JobTypeKey,
  LikedKey,
  ProductNameKey,
  RoleKey,
  WorkgroupKey,
} from "../PromptLibrary/PromptLibrary.keys";
import { PromptTabs } from "../PromptTab/PromptTabs";
import {
  CreatedByFilterWrapper,
  DisplayCategoryFilterWrapper,
  ExtensionFilterWrapper,
  JobTypeFilterWrapper,
  LikedFilterWrapper,
  ProductNameFilterWrapper,
  RoleFilterWrapper,
  SeparatedJobTypeFilterWrapper,
  WorkgroupFilterWrapper,
  groupFilterOptions,
  type BaseFilterWrapper,
  type ExtensionPartyType,
} from "./FilterWrapperClasses";
import {
  CLEAR_FILTER_TOKEN,
  FILTER_CONCATENATE_TOKEN,
  type Filter,
  type FilterName,
} from "./types";
/**
 * Group filter options by group key.
 */
export const groupFilters = (filter: Filter) => {
  return groupFilterOptions(filter.options);
};

/**
 * Populates missing fields for extension prompts. The reason for postprocessing is that 3s sends extension prompts without these fields
 * @param prompts list of prompts
 * @returns prompts with extension prompts having their missing fields populated
 */
export const populateMissingFieldsForPrompts = (
  prompts: Prompt[]
): Prompt[] => {
  return prompts.map((prompt) => {
    // TODO: Daniel, this defaulting logic is breaking user expectation for user-authored prompts. As a temp fix I am only scoping your logic to agent prompts
    // please work with PM to see what's the right defaulting logic that can be applied for all non-msft prompts
    if (isExtensionPrompt(prompt)) {
      if (!prompt.DisplayCategory) {
        prompt = {
          ...prompt,
          DisplayCategory: "Ask",
        };
      }
      if (!prompt.Products) {
        prompt = {
          ...prompt,
          Products: ["BizChat"],
        };
      }
    }
    return prompt;
  });
};

/**
 * Utility function to construct nested options for submenu filters
 * @param options the existing options for the whole filter, will be grouped
 * @returns Array of nested filters, where the displayLabel is the group key and options are the filter options for that group
 */

export const buildFilters = (
  prompts: Prompt[],
  excludes: FilterName[] = [],
  extensionIdMap?: Record<string, string>,
  isRocksteadyUxEnabled?: boolean,
  isMetaOsV2?: boolean,
  disabledFilters?: FilterName[],
  teams?: Group[],
  selectedFilterValues?: Record<FilterName, string[]>
): Filter[] => {
  if (!prompts || prompts.length === 0) {
    return [];
  }
  prompts = populateMissingFieldsForPrompts(prompts);

  const displayCategoryFilterWrapper = new DisplayCategoryFilterWrapper({
    isDisabled: disabledFilters?.includes(DisplayCategoryKey),
  });
  const productNameFilterWrapper = new ProductNameFilterWrapper({
    isDisabled: disabledFilters?.includes(ProductNameKey),
  });
  const likedFilterWrapper = new LikedFilterWrapper({
    isDisabled: disabledFilters?.includes(LikedKey),
  });
  const jobTypeFilterWrapper = new JobTypeFilterWrapper({
    enableNestedOptions: isRocksteadyUxEnabled || isMetaOsV2,
    isDisabled: disabledFilters?.includes(JobTypeKey),
  });
  const createdByFilterWrapper = new CreatedByFilterWrapper({
    isDisabled: disabledFilters?.includes(CreatedByKey),
  });
  const extensionFilterWrapper = new ExtensionFilterWrapper({
    isDisabled: disabledFilters?.includes(ExtensionKey),
    allPrompts: prompts,
    extensionIdMap,
    createTelemetryValueMap: createTelemetryValueMapForExtensions,
  });
  const workgroupFilterWrapper = new WorkgroupFilterWrapper({
    isDisabled: disabledFilters?.includes(WorkgroupKey),
    groups: teams || [],
  });
  const departmentFilterWrapper = new SeparatedJobTypeFilterWrapper({
    isDisabled: disabledFilters?.includes(DepartmentKey),
    metadataName: "Department",
    filterKey: DepartmentKey,
  });
  const industryFilterWrapper = new SeparatedJobTypeFilterWrapper({
    isDisabled: disabledFilters?.includes(IndustryKey),
    metadataName: "Industry",
    filterKey: IndustryKey,
  });
  const educationFilterFactory = new SeparatedJobTypeFilterWrapper({
    isDisabled: disabledFilters?.includes(EducationKey),
    metadataName: "Education",
    filterKey: EducationKey,
  });
  const roleFilterWrapper = new RoleFilterWrapper({
    isDisabled: disabledFilters?.includes(RoleKey),
  });

  prompts.forEach((p) => {
    displayCategoryFilterWrapper.ParsePrompt(p);
    productNameFilterWrapper.ParsePrompt(p);
    jobTypeFilterWrapper.ParsePrompt(p);
    createdByFilterWrapper.ParsePrompt(p);
    extensionFilterWrapper.ParsePrompt(p);
    workgroupFilterWrapper.ParsePrompt(p);
    departmentFilterWrapper.ParsePrompt(p);
    industryFilterWrapper.ParsePrompt(p);
    educationFilterFactory.ParsePrompt(p);
    roleFilterWrapper.ParsePrompt(p);
  });

  const filters = [
    ...(!excludes.includes(ProductNameKey)
      ? [productNameFilterWrapper.GetFilter(selectedFilterValues, prompts)]
      : []),
    ...(!excludes.includes(CreatedByKey)
      ? [createdByFilterWrapper.GetFilter()]
      : []),
    ...(!excludes.includes(DisplayCategoryKey)
      ? [displayCategoryFilterWrapper.GetFilter(selectedFilterValues, prompts)]
      : []),
    ...(!excludes.includes(LikedKey) ? [likedFilterWrapper.GetFilter()] : []),
    ...(!excludes.includes(JobTypeKey)
      ? [jobTypeFilterWrapper.GetFilter()]
      : []),
    ...(!excludes.includes(ExtensionKey)
      ? [extensionFilterWrapper.GetFilter()]
      : []),
    ...(!excludes.includes(WorkgroupKey)
      ? [workgroupFilterWrapper.GetFilter()]
      : []),
    ...(!excludes.includes(DepartmentKey)
      ? [departmentFilterWrapper.GetFilter(selectedFilterValues, prompts)]
      : []),
    ...(!excludes.includes(IndustryKey)
      ? [industryFilterWrapper.GetFilter()]
      : []),
    ...(!excludes.includes(EducationKey)
      ? [educationFilterFactory.GetFilter()]
      : []),
    ...(!excludes.includes(RoleKey) ? [roleFilterWrapper.GetFilter()] : []),
  ].filter((f) => f.options.length > 0) as Filter[];
  return filters;
};
type BuildFilterProps = {
  prompts: Prompt[];
  excludes?: FilterName[];
  extensionIdMap?: Record<string, string>;
  teams?: Group[];
  disabledFilters?: FilterName[];
  excludeSingleOptions?: boolean;
  selectedFilterValues?: Record<FilterName, string[]>;
};

export const buildAllFilters = (props: BuildFilterProps): Filter[] => {
  const {
    prompts,
    excludes,
    extensionIdMap,
    teams,
    disabledFilters,
    excludeSingleOptions,
    selectedFilterValues,
  } = props;
  let filters = buildFilters(
    prompts,
    excludes,
    extensionIdMap,
    true,
    true,
    disabledFilters,
    teams,
    selectedFilterValues
  );

  if (excludeSingleOptions) {
    filters = filters.filter((filter) => {
      return filter.key === CreatedByKey || filter.options.length > 1;
    });
  }

  return filters;
};

export const buildFilterWrappers = ({
  prompts,
  excludes,
  extensionIdMap,
  teams,
  disabledFilters,
  useNestedOptions,
  selectedFilterValues,
}: BuildFilterProps & { useNestedOptions: boolean }): BaseFilterWrapper[] => {
  if (!prompts || prompts.length === 0) {
    return [];
  }
  prompts = populateMissingFieldsForPrompts(prompts);

  const displayCategoryFilterWrapper = new DisplayCategoryFilterWrapper({
    isDisabled: disabledFilters?.includes(DisplayCategoryKey),
  });
  const productNameFilterWrapper = new ProductNameFilterWrapper({
    isDisabled: disabledFilters?.includes(ProductNameKey),
  });
  const likedFilterWrapper = new LikedFilterWrapper({
    isDisabled: disabledFilters?.includes(LikedKey),
  });
  const jobTypeFilterWrapper = new JobTypeFilterWrapper({
    enableNestedOptions: useNestedOptions,
    isDisabled: disabledFilters?.includes(JobTypeKey),
  });
  const createdByFilterWrapper = new CreatedByFilterWrapper({
    isDisabled: disabledFilters?.includes(CreatedByKey),
  });
  const extensionFilterWrapper = new ExtensionFilterWrapper({
    isDisabled: disabledFilters?.includes(ExtensionKey),
    allPrompts: prompts,
    extensionIdMap,
    createTelemetryValueMap: createTelemetryValueMapForExtensions,
  });
  const workgroupFilterWrapper = new WorkgroupFilterWrapper({
    isDisabled: disabledFilters?.includes(WorkgroupKey),
    groups: teams || [],
  });
  const departmentFilterWrapper = new SeparatedJobTypeFilterWrapper({
    isDisabled: disabledFilters?.includes(DepartmentKey),
    metadataName: "Department",
    filterKey: DepartmentKey,
  });
  const industryFilterWrapper = new SeparatedJobTypeFilterWrapper({
    isDisabled: disabledFilters?.includes(IndustryKey),
    metadataName: "Industry",
    filterKey: IndustryKey,
  });
  const educationFilterWrapper = new SeparatedJobTypeFilterWrapper({
    isDisabled: disabledFilters?.includes(EducationKey),
    metadataName: "Education",
    filterKey: EducationKey,
  });
  const roleFilterWrapper = new RoleFilterWrapper({
    isDisabled: disabledFilters?.includes(RoleKey),
  });

  prompts.forEach((p) => {
    displayCategoryFilterWrapper.ParsePrompt(p);
    productNameFilterWrapper.ParsePrompt(p);
    jobTypeFilterWrapper.ParsePrompt(p);
    createdByFilterWrapper.ParsePrompt(p);
    extensionFilterWrapper.ParsePrompt(p);
    workgroupFilterWrapper.ParsePrompt(p);
    departmentFilterWrapper.ParsePrompt(p);
    industryFilterWrapper.ParsePrompt(p);
    educationFilterWrapper.ParsePrompt(p);
    roleFilterWrapper.ParsePrompt(p);
  });

  const filters = [
    ...(!excludes?.includes(ProductNameKey) ? [productNameFilterWrapper] : []),
    ...(!excludes?.includes(CreatedByKey) ? [createdByFilterWrapper] : []),
    ...(!excludes?.includes(DisplayCategoryKey)
      ? [displayCategoryFilterWrapper]
      : []),
    ...(!excludes?.includes(LikedKey) ? [likedFilterWrapper] : []),
    ...(!excludes?.includes(JobTypeKey) ? [jobTypeFilterWrapper] : []),
    ...(!excludes?.includes(ExtensionKey) ? [extensionFilterWrapper] : []),
    ...(!excludes?.includes(WorkgroupKey) ? [workgroupFilterWrapper] : []),
    ...(!excludes?.includes(DepartmentKey) ? [departmentFilterWrapper] : []),
    ...(!excludes?.includes(IndustryKey) ? [industryFilterWrapper] : []),
    ...(!excludes?.includes(EducationKey) ? [educationFilterWrapper] : []),
    ...(!excludes?.includes(RoleKey) ? [roleFilterWrapper] : []),
  ].filter(
    (f) => f.GetFilter(selectedFilterValues, prompts).options.length > 0
  ) as BaseFilterWrapper[];
  return filters;
};

export type FilterDisplayedPromptsProps = {
  prompts: Prompt[];
  filters: Record<FilterName, string[]> | undefined;
  bookmarkOnly?: boolean;
  includeAllPrompts?: boolean;
  groups?: Group[];
  separatePromptOriginForMicrosoft?: boolean;
};
export const filterDisplayedPrompts = (
  props: FilterDisplayedPromptsProps
): Prompt[] => {
  const {
    prompts,
    filters,
    bookmarkOnly,
    includeAllPrompts,
    groups,
    separatePromptOriginForMicrosoft,
  } = props;
  let filterPrompts = prompts;
  if (!includeAllPrompts) {
    filterPrompts = filterPrompts.filter(
      (p) => p.Origin !== "User" && !isWorkgroupPrompt(p)
    );
  }

  if (filters) {
    // Loop through each key-value pair in the filters object
    for (const [key, values] of Object.entries(filters)) {
      // Split string to remove level identifiers (e.g. org label and group label for nested menus) Parse out the clear value from filtering logic
      const splitValues = values
        .map((value) => {
          const split = value.split(FILTER_CONCATENATE_TOKEN);
          return [split[0], split.length > 1 ? split[1] : ["NA"]];
        })
        .filter((value) => value[0] !== CLEAR_FILTER_TOKEN);

      // Need separated values to prevent microsoft prompts displaying when a duplicate department for tenant is selected (and vice versa)
      // E.g. both Microsoft and Tenant prompts have a department called "Engineering"
      const microsoftSplitValues = splitValues.filter(
        ([_, groupLabel]) => groupLabel === "FromMicrosoft"
      );
      const tenantSplitValues = splitValues.filter(
        ([_, groupLabel]) => groupLabel === "FromOrganization"
      );

      // Filterable values are simpler for single level filters
      const filterableValues = values
        .map((value) => {
          return value.split(FILTER_CONCATENATE_TOKEN)[0];
        })
        .filter((value) => value !== CLEAR_FILTER_TOKEN);

      switch (key) {
        case "displayCategory":
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  filterableValues.includes(p.DisplayCategory)
                )
              : filterPrompts;
          break;
        case "products/name":
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  filterableValues.some((item) => p.Products.includes(item))
                )
              : filterPrompts;
          break;
        case "jobType":
          {
            filterPrompts =
              splitValues.length > 0
                ? filterPrompts.filter((p) => {
                    switch (p.Origin) {
                      case "Microsoft":
                      case "Global":
                        return microsoftSplitValues.some(([value]) => {
                          return p.Metadata?.find((m) => {
                            return m.Value === value;
                          });
                        });

                      case "Tenant":
                        return tenantSplitValues.some(([value]) => {
                          return p.Metadata?.find((m) => {
                            return m.Value === value;
                          });
                        });
                      default:
                        return splitValues.some(([value]) => {
                          return p.Metadata?.find((m) => {
                            return m.Value === value;
                          });
                        });
                    }
                  })
                : filterPrompts;
          }
          break;
        case "extension":
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  filterableValues.some((item) => p.ProviderName === item)
                )
              : filterPrompts;
          break;
        case "workgroup": {
          const groupIds = groups?.map((g) => {
            if (filterableValues.includes(g.Id)) {
              return g.Id;
            }
          });
          if (filterableValues.length > 0 && groupIds && groupIds.length > 0) {
            filterPrompts = filterPrompts.filter((prompt) =>
              prompt.Groups?.some((g) => groupIds?.includes(g.Id))
            );
            break;
          }
          break;
        }
        case "createdBy": {
          let filterFuncs: ((prompt: Prompt, value: string) => boolean)[] = [];
          if (filterableValues.includes("CreatedByMe" as CreatedByCategory)) {
            filterFuncs = filterFuncs.concat((p, _v) =>
              isNonSharedUserPrompt(p)
            );
          }
          if (
            filterableValues.includes("CreatedByMicrosoft" as CreatedByCategory)
          ) {
            filterFuncs = filterFuncs.concat((p, _v) =>
              separatePromptOriginForMicrosoft
                ? isMicrosoftOrigin(p)
                : isMicrosoftPrompt(p)
            );
          }
          if (
            filterableValues.includes("CreatedByTenant" as CreatedByCategory)
          ) {
            filterFuncs = filterFuncs.concat((p, _v) => isTenantPrompt(p));
          }
          if (
            filterableValues.includes("CreatedByOthers" as CreatedByCategory)
          ) {
            filterFuncs = filterFuncs.concat((p, _v) => isWorkgroupPrompt(p));
          }
          if (
            filterableValues.length > 0 &&
            !(
              filterableValues.length == 1 &&
              filterableValues.includes("CreatedByAll")
            )
          ) {
            filterFuncs = filterFuncs.concat(
              (prompt: Prompt, value: string) => {
                return (prompt as ExtensionPrompt).DeveloperName === value;
              }
            );
          }

          filterPrompts =
            filterableValues.length > 0 && filterFuncs.length > 0
              ? filterPrompts.filter((p) => {
                  return filterFuncs.some((filterFunc) => {
                    return filterableValues.some((value) => {
                      return filterFunc(p, value);
                    });
                  });
                })
              : filterPrompts;
          break;
        }
        case "liked":
          filterPrompts =
            filterableValues.length > 0 && filterableValues[0] === "Liked"
              ? filterPrompts.filter(isLikedPrompt)
              : filterPrompts;
          break;
        case "origin":
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  filterableValues.includes(p.Origin as string)
                )
              : filterPrompts;
          break;
        case "metadata":
          // Filtering out prompts based on the presence of specific metadata keys
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  p.Metadata?.some((m) => filterableValues.includes(m.Name))
                )
              : filterPrompts;
          break;
        case "department":
          // Filtering out prompts based on the presence of specific metadata keys
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  p.Metadata?.some(
                    (m) =>
                      m.Name === "Department" &&
                      filterableValues.includes(m.Value)
                  )
                )
              : filterPrompts;
          break;
        case "role":
          // Filtering out prompts based on the presence of specific metadata keys
          filterPrompts =
            filterableValues.length > 0
              ? filterPrompts.filter((p) =>
                  p.Metadata?.some(
                    (m) =>
                      m.Name === "Role" && filterableValues.includes(m.Value)
                  )
                )
              : filterPrompts;
          break;
      }
    }
  }

  if (bookmarkOnly) {
    filterPrompts = filterPrompts.filter(
      (prompt) => prompt.UserActivity?.Favorite?.IsFavorite
    );
  }

  return filterPrompts;
};

/**
 * Generates lookup map matching provider name to generated string that will be logged in telemetry for extensions since extension names are considered customer data
 * @param extensions Record of extension name and associated extension and party type
 * @returns Lookup table with provider name as key and generic values for telemetry as values (e.g. Data_ExtensionPlugIn_1p, Data_ExtensionPlugIn_3p, Data_ExtensionDC_1p, Data_ExtensionDC_3p)
 */
const createTelemetryValueMapForExtensions = (
  extensions: Record<string, ExtensionPartyType>
): Record<string, string> => {
  return Object.entries(extensions).reduce(
    (result: Record<string, string>, [key, value]) => {
      result[key] = generateExtensionTelemetryValue(value);
      return result;
    },
    {}
  );
};

/**
 * Generates string from origin and party type that will be logged in telemetry for extensions inplace of extension name since it is considered customer data
 * @param extensionString extension name
 * @param partyString party type
 * @returns string for telemetry (e.g. Data_ExtensionPlugIn_1p, Data_ExtensionPlugIn_3p, Data_ExtensionDC_1p, Data_ExtensionDC_3p)
 */
export const generateExtensionTelemetryValue = (
  extensionPartyType: ExtensionPartyType
): string => {
  // TODO: Remove logging extensionType and partyType in telemetry once we are good with titleId logging.
  if (extensionPartyType.ExtensionLoggableId) {
    return extensionPartyType.ExtensionLoggableId;
  }
  const extensionType = getExtensionType(extensionPartyType.Origin as string);
  const partyType = getPartyType(
    extensionPartyType.ProviderPartyType as string
  );
  return `Data_Extension${extensionType}_${partyType}`;
};

/**
 * Generates string from origin
 * @param originString
 * @returns string for extension type (e.g. PlugIn, DC, User)
 */
const getExtensionType = (originString: string): string => {
  switch (originString) {
    case "Plugin":
      return "PlugIn";
    case "GPT":
      return "DC";
    case "User":
      return "User";
    default:
      return "Unknown";
  }
};

/**
 * Generates string from party type
 * @param partyString
 * @returns string for party type (e.g. 1p, 3p)
 */
const getPartyType = (partyString: string): string => {
  switch (partyString) {
    case "FirstParty":
      return "1p";
    case "ThirdParty":
      return "3p";
    default:
      return "Unknown";
  }
};

/**
 * Checks if rightFacets contains leftFacets
 * @param leftFacets
 * @param rightFacets
 * @returns true if rightFacets contains leftFacets, false otherwise
 */
export const facetsContain = (
  leftFacets: Record<string, string[]>,
  rightFacets: Record<string, string[]>
): boolean => {
  return Object.entries(leftFacets).every(([leftKey, leftValues]) => {
    const rightValues = rightFacets[leftKey];
    if (!rightValues) {
      return false;
    }

    return leftValues.every((v) => rightValues.includes(v));
  });
};

export const isFilterEnabled = (
  filterName: FilterName,
  selectedTab: PromptTabs,
  isAgentPromptFilteringEnabled?: boolean,
  isMetaOsV2?: boolean
): boolean => {
  const AllPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    ExtensionKey,
  ];

  const MicrosoftPromptsTabFilters = [
    ProductNameKey,
    DisplayCategoryKey,
    JobTypeKey,
    ExtensionKey,
    RoleKey,
  ];

  const WorkPlaceTabFilters = [
    ProductNameKey,
    DisplayCategoryKey,
    JobTypeKey,
    ExtensionKey,
    RoleKey,
  ];

  const CombinedPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    ExtensionKey,
    CreatedByKey,
    RoleKey,
  ];
  const SearchTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    ExtensionKey,
    CreatedByKey,
    WorkgroupKey,
  ];
  const SavedPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    LikedKey,
    CreatedByKey,
    isAgentPromptFilteringEnabled ? ExtensionKey : "",
  ];
  const SavedPromptsNavItemFilters = [ProductNameKey, CreatedByKey];
  const WorkgroupPromptsTabFilters = [WorkgroupKey, CreatedByKey];
  const FromTeamsPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    WorkgroupKey,
    CreatedByKey,
  ];
  const AgentPromptsTabFilters = [ExtensionKey];
  const LikedPromptsTabFilters = [
    CreatedByKey,
    ProductNameKey,
    DisplayCategoryKey,
    JobTypeKey,
    ExtensionKey,
  ];

  switch (selectedTab) {
    case PromptTabs.AllPrompts:
      return AllPromptsTabFilters.includes(filterName);
    case PromptTabs.CombinedPrompts:
      return CombinedPromptsTabFilters.includes(filterName);
    case PromptTabs.BrowsePrompts:
      return CombinedPromptsTabFilters.includes(filterName);
    case PromptTabs.WorkplacePrompts:
      return WorkPlaceTabFilters.includes(filterName);
    case PromptTabs.MicrosoftPrompts:
      return MicrosoftPromptsTabFilters.includes(filterName);
    case PromptTabs.SearchPrompts:
      return SearchTabFilters.includes(filterName);
    case PromptTabs.SavedPrompts:
      return isMetaOsV2
        ? SavedPromptsNavItemFilters.includes(filterName)
        : SavedPromptsTabFilters.includes(filterName);
    case PromptTabs.GroupPrompts:
      return isMetaOsV2
        ? FromTeamsPromptsTabFilters.includes(filterName)
        : WorkgroupPromptsTabFilters.includes(filterName);
    case PromptTabs.AgentPrompts:
      return AgentPromptsTabFilters.includes(filterName);
    case PromptTabs.LikedPrompts:
      return LikedPromptsTabFilters.includes(filterName);
  }

  return false;
};
